import type { Database } from '@/types/supabase';
import type { FinancialCategory } from '@/entities/FinancialCategory';
import type { PlannedCategory } from '@/entities/PlannedCategory';

/**
 * Tipos base para categorias financeiras
 */
export type CategoryType = 'income' | 'expense';

/**
 * Interface para dados de transações financeiras
 */
export interface FinancialTransaction {
  id: string;
  amount: number;
  date: string;
  category_id: string;
  account_id: string;
  monthly_plan_id: string;
  description?: string;
  is_investment: boolean;
}

/**
 * Interface para subcategoria com totais calculados
 */
export interface SubcategoryWithTotals {
  id: string;
  name: string;
  parent_id: string;
  type: CategoryType;
  planned_amount: number;
  spent_amount: number;
  received_amount: number;
  total_spent: number;
  percentage: number;
  account_id: string;
  created_at: string;
  updated_at: string;
  transactions?: FinancialTransaction[];
}

/**
 * Interface para categoria principal com dados agregados
 */
export interface CategoryWithAggregatedData {
  id: string;
  name: string;
  type: CategoryType;
  parent_id?: string | null;
  account_id: string;
  
  // Valores planejados e executados
  planned_amount: number;
  spent_amount: number;
  received_amount: number;
  
  // Totais calculados (incluindo subcategorias)
  total_planned: number;
  total_spent: number;
  total_received: number;
  
  // Metadados
  percentage: number;
  has_subcategories: boolean;
  subcategories: SubcategoryWithTotals[];
  transactions?: FinancialTransaction[];
  
  // Dados do plano mensal
  planned_category_id?: string;
  monthly_plan_id?: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
}

/**
 * Interface para dados de progresso de categoria (usado no CategoryProgress)
 */
export interface CategoryProgressData {
  id: string;
  name: string;
  type: CategoryType;
  planned: number;
  spent: number;
  received: number;
  percentage: number;
  parent_id?: string;
  children?: CategoryProgressData[];
}

/**
 * Interface para dados do gráfico de pizza (usado no ExpensePieChart)
 */
export interface PieChartCategoryData {
  id: string;
  name: string;
  value: number;
  planned?: number;
  percentage?: number;
  records: {
    id: string;
    description?: string;
    date: string;
    amount: number;
  }[];
  subcategories: {
    id: string;
    name: string;
    totalSpent: number;
    planned?: number;
    percentage?: number;
    records: {
      id: string;
      description?: string;
      date: string;
      amount: number;
    }[];
  }[];
  hasSubcategories: boolean;
}

/**
 * Interface para filtros de busca de categorias
 */
export interface CategoryFilters {
  account_id: string;
  monthly_plan_id?: string;
  type?: CategoryType;
  parent_id?: string | null;
  include_subcategories?: boolean;
  date_range?: {
    start_date: string;
    end_date: string;
  };
}

/**
 * Interface para resposta do serviço de categorias
 */
export interface CategoryServiceResponse<T> {
  data: T;
  success: boolean;
  error?: string;
  cached?: boolean;
  timestamp: number;
}

/**
 * Interface para configuração do cache
 */
export interface CacheConfig {
  defaultTTL: number; // Time to live padrão em milissegundos
  maxSize: number; // Número máximo de entradas no cache
  cleanupInterval: number; // Intervalo de limpeza em milissegundos
  enableMetrics: boolean; // Habilitar métricas de performance
  enableCompression: boolean; // Habilitar compressão de dados
}

/**
 * Interface para entrada do cache
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  accessCount: number;
  lastAccessed: number;
  tags: string[];
  compressed: boolean;
}

/**
 * Interface para estatísticas de categorias
 */
export interface CategoryStatistics {
  total_categories: number;
  total_subcategories: number;
  total_planned: number;
  total_spent: number;
  total_received: number;
  categories_over_budget: number;
  categories_under_budget: number;
  average_completion_rate: number;
}

/**
 * Interface para dados completos do dashboard
 */
export interface DashboardCategoryData {
  categories: CategoryWithAggregatedData[];
  statistics: CategoryStatistics;
  pie_chart_data: PieChartCategoryData[];
  progress_data: CategoryProgressData[];
  last_updated: string;
}

/**
 * Enum para tipos de erro do serviço
 */
export enum CategoryServiceErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  CACHE_ERROR = 'CACHE_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Interface para erro customizado do serviço
 */
export interface CategoryServiceError {
  type: CategoryServiceErrorType;
  message: string;
  details?: any;
  timestamp: number;
}
